import { useEffect, useState, useSyncExternalStore } from 'react';
import { createUsBrowserMarketClient } from '@/lib/polymarketUs/browserMarketClient';
import type { UsMarketBookData } from '@/lib/polymarketUs/marketClient';
import { UsClientError } from '@/lib/polymarketUs/tradingClient';
import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';
import { US_BOOK_POLL_MS } from './useUsGameVenue';

const subscribeAuthEpoch = (notify: () => void) => useAuthStore.subscribe(notify);
const subscribePageVisibility = (notify: () => void) => {
  document.addEventListener('visibilitychange', notify);
  return () => document.removeEventListener('visibilitychange', notify);
};
const readPageVisibility = () => document.visibilityState === 'visible';

/** #1234 — the public US depth of one market for the board's expanded ladder,
 * read every US_BOOK_POLL_MS while `slug` is set and the page is visible.
 * Display only (`readPublicBook`): no account, never an execution input.
 * Null while off, before the first read, after a failed read and across a
 * login change: an old book is never painted as the current one. The caller
 * still judges validity (`usBookCurrent`) on every render. */
export function useUsLadderBook(slug: string | null): UsMarketBookData | null {
  const pageVisible = useSyncExternalStore(subscribePageVisibility, readPageVisibility);
  const sessionEpoch = useSyncExternalStore(subscribeAuthEpoch, getAuthSessionEpoch);
  // Its own client: a read supersedes earlier reads on the same instance.
  const [client] = useState(createUsBrowserMarketClient);
  const key = slug && pageVisible ? `${sessionEpoch}|${slug}` : null;
  const [read, setRead] = useState<{ key: string; book: UsMarketBookData | null } | null>(null);
  useEffect(() => {
    if (!key || !slug) return;
    let cancelled = false;
    let pending = false;
    const readBook = () => {
      // The real client supersedes earlier requests. A healthy response slower
      // than the poll interval must still get a turn to reach the screen.
      if (cancelled || pending) return;
      pending = true;
      void client.readPublicBook(slug)
      .then((book) => { if (!cancelled) setRead({ key, book }); })
      .catch((err) => {
        if (cancelled || (err instanceof UsClientError && err.code === 'US_CLIENT_CONTEXT_CHANGED')) return;
        setRead({ key, book: null });
      })
      .finally(() => { pending = false; });
    };
    void readBook();
    const id = setInterval(() => void readBook(), US_BOOK_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
      client.invalidate();
    };
  }, [client, key, slug]);
  return read?.key === key ? read.book : null;
}
